import { usePost } from 'hooks/use-post';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';

import Dialog from 'components/dialog';
import Select from 'components/select';
import Switch from 'components/switch';
import FlexBox from 'components/flex-box';
import TextField from 'components/text-field';
import NumberField from 'components/number-field';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { CreateAeratorInput } from '../types/create-aerator-input.type';
import { kgToLbs, lbsToKg } from 'views/self-service/utils/convertLbsKg';
import { Aerator } from 'views/self-service/features/aerators/types/aerators.type';
import { postAerator } from 'views/self-service/features/aerators/api/post-aerator';
import { isValidDecimal, isValidInteger, isValidPositive } from 'utils/validate-numbers';
import { AeratorTypes } from 'views/self-service/features/aerators/types/aerator-type.enum';
import { AeratorSubTypes } from 'views/self-service/features/aerators/types/aerator-sub-type.enum';

type CreateAeratorProps = {};

const MAX_HORSE_POWER = 40;
const MAX_SUPPORTED_BIOMASS = 20000;

const PAGE_NAME_ID = 'selfService.aerators';

const CreateAerator: React.FC<CreateAeratorProps> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  //#region STATE
  const [enableKG, setEnableKg] = useState<boolean>(false);
  //#endregion STATE

  //#region FORMS
  const { handleSubmit, control, getValues, setValue } = useForm<CreateAeratorInput>({
    defaultValues: {
      name: '',
      brand: '',
      type: '',
      subType: '',
      horsepower: '',
      biomassSupported: '',
    },
    shouldUnregister: true,
  });
  //#endregion FORMS

  //#region POST DATA
  const createAerator = usePost<Aerator, { request: CreateAeratorInput }>(
    (request) => postAerator(account.company!.companyId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      setEnableKg(false);
      queryClient.invalidateQueries({
        queryKey: ['getAerators']
      });
      dialogRef?.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  //#region HANDLERS
  const handleCreateAerator = (request: CreateAeratorInput) => {
    createAerator.mutate({
      request: {
        ...request,
        horsepower: Number(request.horsepower),
        biomassSupported: enableKG ? Number(request.biomassSupported) : Math.round(lbsToKg(Number(request.biomassSupported))),
      },
    });
  };

  const onKeyDown = (e: any) => e.stopPropagation();

  const calculateBiomass = () => {
    let biomass = getValues('biomassSupported');
    let convertedBiomass = enableKG ? kgToLbs(Number(biomass)) : Math.round(lbsToKg(Number(biomass)));

    setValue('biomassSupported', convertedBiomass === 0 ? '' : convertedBiomass);
  };

  //#endregion HANDLERS

  return (
    <Box flexDirection={'column'} display={'flex'} pb={3}>
      <Dialog
        maxWidth="sm"
        ref={dialogRef}
        sx={{ zIndex: 1510 }}
        onClose={() => setEnableKg(false)}
        onKeyDown={(e) => e.key === 'Tab' && e.stopPropagation()}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.createAerator.dialog.title` })}
        button={<Button fullWidth>{intl.formatMessage({ id: `${PAGE_NAME_ID}.createAerator.btnTitle` })}</Button>}>
        <form onSubmit={handleSubmit(handleCreateAerator)}>
          {createAerator.isPending && <LoadingSpinner overlay />}
          <FlexBox>
            <TextField
              dark
              fullWidth
              stopPropagation
              onKeyDown={onKeyDown}
              controller={{
                name: 'name',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createAerator.dialog.name` })}
            />
            <TextField
              dark
              fullWidth
              type="string"
              controller={{
                name: 'brand',
                control: control,
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createAerator.dialog.brand` })}
            />
            <Box display={'flex'} gap={4} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
              <Select
                dark
                nested
                fullWidth
                stopPropagation
                controller={{
                  name: 'type',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                  },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.updateAerator.dialog.type` })}>
                {AeratorTypes?.map(({ id, name }, index) => (
                  <RadioSelectItem key={index} value={id}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.types.${name}`} />
                  </RadioSelectItem>
                ))}
              </Select>
              <Select
                dark
                nested
                fullWidth
                stopPropagation
                controller={{
                  name: 'subType',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                  },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.updateAerator.dialog.subType` })}>
                {AeratorSubTypes.map(({ id, name }, index) => (
                  <RadioSelectItem key={index} value={id}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.types.${name}`} />
                  </RadioSelectItem>
                ))}
              </Select>
            </Box>
            <NumberField
              dark
              fullWidth
              stopPropagation
              onKeyDown={onKeyDown}
              controller={{
                name: 'horsepower',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (value: number) =>
                      isValidPositive(value) ? true : intl.formatMessage({ id: 'form.rules.invalidValue' }),
                    between: (value: number) =>
                      isValidInteger(value) && Number(value) <= MAX_HORSE_POWER
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: 0, upper: MAX_HORSE_POWER }),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.updateAerator.dialog.horsepower` })}
            />
            <NumberField
              dark
              fullWidth
              onKeyDown={onKeyDown}
              stopPropagation
              endAdornment={enableKG ? 'kg' : 'lbs'}
              controller={{
                name: 'biomassSupported',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (value: number) =>
                      isValidDecimal(value) && Math.round(lbsToKg(value))
                        ? true
                        : intl.formatMessage({ id: 'form.rules.invalidValue' }),
                    between: (value: number) =>
                      Math.round(lbsToKg(value)) || (isValidInteger(value) && Number(value) <= MAX_SUPPORTED_BIOMASS)
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: 0, upper: MAX_SUPPORTED_BIOMASS }),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createAerator.dialog.biomassSupported` })}
            />
            <Switch
              dark
              value={enableKG}
              onChange={() => {
                setEnableKg((prev) => !prev);
                calculateBiomass();
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createAerator.dialog.switchLabel` })}
            />
            <Box display={'flex'} mt={5} justifyContent={'end'}>
              <Button type="submit" fullWidth={isMobile}>
                <FormattedMessage id={`${PAGE_NAME_ID}.createAerator.dialog.button`} />
              </Button>
            </Box>
          </FlexBox>
        </form>
      </Dialog>
    </Box>
  );
};

export default React.memo(CreateAerator);
